import type { VickiAction } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Check, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { JsonBlock } from '@/components/code-block';
import { RelativeTime } from '@/components/relative-time';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** The tool VICKI uses to hand work to a desk (docs/features/front-desk.md). */
const DELEGATE = 'delegate_work';
/** Her proposal to change a goal: only owners and admins decide it. */
const GOAL = 'goal_change';

const STATUS: Record<VickiAction['status'], { label: string; tone: 'amber' | 'green' | 'muted' | 'red' | 'outline' }> = {
  pending_approval: { label: t('Waits for you'), tone: 'amber' },
  running: { label: t('Running'), tone: 'outline' },
  succeeded: { label: t('Done'), tone: 'green' },
  failed: { label: t('Failed'), tone: 'red' },
  rejected: { label: t('Rejected'), tone: 'muted' },
};

/** What VICKI wants to do, in words: handing work to a desk, or a tool of the account's API. */
function what(action: VickiAction) {
  if (action.tool === DELEGATE && action.arguments.own) return t('Take on as a task: {title}', { title: String(action.arguments.title ?? '') });
  if (action.tool === DELEGATE) return tr('Hand to {desk}: {title}', { desk: String(action.arguments.desk ?? ''), title: String(action.arguments.title ?? '') });
  if (action.tool === GOAL) {
    const title = String(action.arguments.title ?? action.arguments.goal ?? '');
    const change = String(action.arguments.change);
    return change === 'add'
      ? t('New goal: {title}', { title })
      : change === 'reached'
        ? t('Goal reached: {title}', { title })
        : change === 'dropped'
          ? t('Drop goal: {title}', { title })
          : t('Change goal: {title}', { title });
  }
  return action.tool;
}

/**
 * A change VICKI asked for in her chat or in one of her tasks (docs/features/front-desk.md#approval-cards): while it
 * waits, anyone of the account approves it (it runs with their rights) or rejects it with a word for VICKI; then a line
 * says how it ended.
 */
export function VickiActionCard({ conversationId, workItemId, actionId }: { conversationId?: string; workItemId?: string; actionId: string }) {
  const key = workItemId ? qk.vickiTaskActions(workItemId) : qk.vickiActions(conversationId ?? '');
  const actions = useQuery({
    queryKey: key,
    queryFn: () => (workItemId ? api.frontDesk.taskActions(workItemId) : api.frontDesk.actions(conversationId!)),
    // A card shows up with its message; a decision elsewhere shows up within seconds.
    refetchInterval: (q) => (q.state.data?.some((a) => a.status === 'pending_approval' || a.status === 'running') ? 5000 : false),
  });
  const action = actions.data?.find((a) => a.id === actionId);
  const [comment, setComment] = useState('');
  const decide = useApiMutation((decision: 'approve' | 'reject') => api.frontDesk.decide(actionId, decision, comment.trim() || undefined), {
    invalidate: [key, ...(workItemId ? [qk.workItem(workItemId)] : [])],
    success: (done) => (done.status === 'rejected' ? t('Rejected — VICKI will be told.') : done.status === 'succeeded' ? t('Approved and done — VICKI goes on.') : t('Approved, but it failed — VICKI will look into it.')),
  });
  if (!action) return null;
  if (action.status !== 'pending_approval') {
    const status = STATUS[action.status];
    return (
      <div className="flex justify-center">
        <div className="inline-flex max-w-[90%] items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="size-3.5 shrink-0" />
          <span className="truncate">{what(action)}</span>
          <Badge tone={status.tone}>{status.label}</Badge>
        </div>
      </div>
    );
  }
  const busy = decide.isPending;
  const args = action.tool === DELEGATE || action.tool === GOAL ? null : action.arguments;
  return (
    <div className="rounded-xl border-2 border-orange-300 bg-orange-50/70 p-3 sm:p-4 dark:border-orange-800 dark:bg-orange-950/30">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-orange-500/15 text-orange-600 max-sm:hidden dark:text-orange-300">
          <ShieldAlert className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{tr('VICKI wants to do this – nothing has happened yet · asked {time}', { time: <RelativeTime value={action.requestedAt} /> })}</p>
          <div className="mt-1 text-sm font-semibold break-words">{what(action)}</div>
          {action.explanation && <p className="mt-1 text-sm">{action.explanation}</p>}
          {action.tool === DELEGATE && action.arguments.description ? (
            <p className="mt-2 line-clamp-6 text-xs whitespace-pre-wrap text-muted-foreground">{String(action.arguments.description)}</p>
          ) : null}
          {action.tool === GOAL && (action.arguments.target || action.arguments.description || action.arguments.due_date) ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {action.arguments.target ? <div>{t('Target: {target}', { target: String(action.arguments.target) })}</div> : null}
              {action.arguments.due_date ? <div>{t('Due by {date}', { date: String(action.arguments.due_date) })}</div> : null}
              {action.arguments.description ? <div className="whitespace-pre-wrap">{String(action.arguments.description)}</div> : null}
            </div>
          ) : null}
          {action.tool === GOAL && <p className="mt-2 text-xs text-muted-foreground">{t('Only owners and admins decide about goals.')}</p>}
          {args && Object.keys(args).length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-muted-foreground">{t('Details')}</summary>
              <JsonBlock value={args} className="mt-1" />
            </details>
          )}
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('A word for VICKI (optional)')} rows={1} className="mt-3 min-h-9 bg-card" />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => decide.mutate('approve')} loading={busy && decide.variables === 'approve'} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-600/90">
              <Check /> {t('Approve')}
            </Button>
            <Button variant="outline" onClick={() => decide.mutate('reject')} loading={busy && decide.variables === 'reject'} disabled={busy}>
              <X /> {t('Reject')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
